import { stripAuthErrorParams } from './authRedirect';
import { createCloudBackend } from './cloudBackend';
import { createLocalBackend, type SaveBackend } from './localBackend';
import { createSupabaseRemote } from './supabaseRemote';
import { publicSupabaseConfig } from './supabaseConfig';

/** Cloud when a production build, or an explicit dev opt-in, has public keys and auth answers. Otherwise the local save. */
export async function createPlayBackend(): Promise<SaveBackend> {
  const config = publicSupabaseConfig();
  if (!config || typeof window === 'undefined') return createLocalBackend();
  const backend = createCloudBackend(createSupabaseRemote(config), {
    device: deviceLabel(),
    redirectTo: `${window.location.origin}${window.location.pathname}`,
    redirectHref: window.location.href,
  });
  try {
    await withTimeout(backend.prepare?.() ?? Promise.resolve(), 8000);
    if (backend.account().collision) {
      window.history.replaceState({}, '', stripAuthErrorParams(window.location.href));
    }
    return backend;
  } catch (error) {
    backend.dispose?.();
    const local = createLocalBackend();
    const detail = error instanceof Error ? error.message : '';
    const notice =
      detail && detail !== 'unknown' && detail.length < 160
        ? `Cloud save is unavailable (${detail}). Playing on this browser.`
        : 'Cloud save is unavailable. Playing on this browser.';
    local.setStartupNotice?.(notice);
    return local;
  }
}

function deviceLabel(): string {
  const agent = navigator.userAgent.replace(/\s+/g, ' ').slice(0, 60);
  return `web ${agent}`.slice(0, 80);
}

function withTimeout(work: Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('cloud_timeout')), ms);
    work.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}
